import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import { CHAT_HISTORY_KEY } from "./chatHistory";
import { PREFERENCES_KEY } from "./userPreferences";
import { isRole, type Role } from "./permissions";

export interface SessionUser {
  name: string;
  role: Role;
  // Optional fields populated by the backend's /auth/session response since
  // the SessionUser.id claim was added. Pre-deployed legacy tokens may have
  // these as undefined — guards against that with optional chaining.
  id?: string;
  email?: string;
}

interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider(props: { children: React.ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ user: SessionUser }>("/auth/session");
      if (res.data?.user && isRole(res.data.user.role)) {
        setUser(res.data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Re-validate the session in the background while a user is signed in, so a
  // demotion (new role on /auth/session → setUser) or a revocation (401 →
  // setUser(null) → redirect) takes effect without a manual page reload. We
  // re-check on tab focus, on visibility change (back to "visible"), and on a
  // 60s poll. Keyed on `isSignedIn` (a boolean) so the listeners/interval are
  // only attached while logged in — never on the sign-in page — and the effect
  // doesn't tear down + rebuild on every unrelated `user` object change.
  // `refresh` is stable (useCallback with []), so it never re-runs the effect.
  const isSignedIn = user != null;
  useEffect(() => {
    if (!isSignedIn) return;

    const revalidate = (): void => {
      void refresh();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") revalidate();
    };

    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(revalidate, 60_000);

    return () => {
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [isSignedIn, refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore; we clear locally regardless
    } finally {
      setUser(null);
      // Clear browser-side state — chat history can contain UBO / director
      // info (compliance-sensitive) and would otherwise leak to the next
      // user on a shared workstation. Preferences cleared too for parity.
      try {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(CHAT_HISTORY_KEY);
          window.localStorage.removeItem(PREFERENCES_KEY);
        }
      } catch {
        /* ignore quota / serialization errors */
      }
    }
  }, []);

  const value = useMemo<SessionState>(
    () => ({ user, loading, refresh, signOut }),
    [user, loading, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

export function useLogout(): () => Promise<void> {
  const { signOut } = useSession();
  const navigate = useNavigate();
  return useCallback(async () => {
    await signOut();
    navigate("/auth/signin", { replace: true });
  }, [signOut, navigate]);
}
